import React, { FC, useMemo } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { renderRichText } from "gatsby-source-contentful/rich-text"

import { TestimonialBlockFragmentFragment } from "@graphqlTypes"

import AvatarWithName from "@components/AvatarWithName/AvatarWithName"
import { AVATAR_SIZES } from "@components/Avatar/Avatar"
import { Scrubber } from "@helpscout/hs-marketing-shared-components"

import { generateConditionalClassName } from "@utils"

import "./TestimonialBlockScroller.scss"

type TestimonialBlockScrollerProps = {
  includeFeatured: TestimonialBlockFragmentFragment["includeFeatured"],
  items: TestimonialBlockFragmentFragment["items"],
}

const TestimonialBlockScroller: FC<TestimonialBlockScrollerProps> = ({
  includeFeatured,
  items,
}) => {
  if (!items || items.length === 0) return null

  const featuredQuote = includeFeatured ? items[0] : null
  const itemsLength = includeFeatured ? items.length - 1 : items.length
  const splitAtIndex = itemsLength <= 10 ? itemsLength : Math.floor(itemsLength / 2)
  const quotesSplit = useMemo(() => items.reduce<Array<TestimonialBlockScrollerProps["items"]>>((acc, item, i) => {
    if (!item) return acc

    if (i === 0 && includeFeatured) return acc

    if (i <= splitAtIndex) return [
      [...(acc[0] || []), item],
    ]

    if (i > splitAtIndex) return [
      acc[0],
      [...(acc[1] || []), item]
    ]

    return acc
  }, []), [items])

  return (
    <div
      className={generateConditionalClassName({
        "TestimonialBlockScroller": true,
        "include-featured": !!includeFeatured,
        "has-no-items": itemsLength === 0,
      })}
    >
      {includeFeatured && featuredQuote && (
        <div className="FeaturedQuote">
          <div className="container">
            <div className="grid">
              <blockquote className="column column--2-12-L">
                {renderRichText({ raw: featuredQuote.quote?.raw, references: [] })}
              </blockquote>
              <div className="column">
                <div className="avatar-vertical text-align--center">
                  {featuredQuote?.person?.image?.imgixImage?.gatsbyImageData && (
                    <GatsbyImage
                      alt={featuredQuote.person.name || ""}
                      className="author-image"
                      image={featuredQuote.person.image.imgixImage.gatsbyImageData}
                    />
                  )}
                  <h6 className="type--headline-medium-S">{featuredQuote.person?.name || ""}</h6>
                  <p className="type--text-S font-style--italic margin-bottom--0">{featuredQuote.person?.jobTitle}</p>
                  {featuredQuote.companyLogo?.logo?.imgixImage?.gatsbyImageData && (
                    <GatsbyImage
                      alt={featuredQuote.person?.company || ""}
                      className="company-logo"
                      image={featuredQuote.companyLogo.logo.imgixImage.gatsbyImageData}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {quotesSplit && quotesSplit.length > 0 && (
        <Scrubber>
          {(ref, hiddenPct) => (
            <div className="QuoteRows" ref={ref}>
              {quotesSplit.map((quotes, i) => !quotes ? null : (
                <div
                  className={`QuoteRows--row ${i === 1 ? "to-right" : "to-left"}`}
                  style={{
                    transform: i === 1 ?
                      `translate(${-20 + hiddenPct * 20}vw)` :
                      `translate(${hiddenPct * -20}vw)`
                  }}
                >
                  {quotes.map((quote, i) => !quote ? null : (
                    <div className="QuoteRows--quote" key={`${quote.person?.name || ""}--${i}`}>
                      {quote.quote?.raw && (
                        <blockquote>
                          {renderRichText({ raw: quote.quote.raw, references: [] })}
                        </blockquote>
                      )}
                      {quote.person?.image && (
                        <AvatarWithName
                          bio={`${quote.person?.jobTitle} at ${quote.person?.company}`}
                          name={quote.person?.name || ""}
                          image={quote.person?.image}
                          size={AVATAR_SIZES.S}
                        />
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </Scrubber>
      )}
    </div>
  )
}

export default TestimonialBlockScroller
